import * as React from "react";
import DisplayFormSection from "./DisplayFormSection";
import { Spin, Divider, Button, Modal, Icon, Alert } from "antd";
import SignatureSection from "./SignatureSection";
import { IReferenceValue } from "../../../model/form/ReferenceValue";
import { IFormVersion } from "../../../model/form/FormVersion";
import { IFormInputValueValidationError } from "../../../model/validation/FormInputValueValidationError";
import { IFormSectionPresentationModel } from "../../../presenter/form/model/FormSectionPresentationModel";
import Text from "antd/lib/typography/Text";
import { IReport1Applicant } from "../../../model/report/Report1Applicant";
import { IFormVersionItem } from "../../../model/report/FormVersionItem";
import { IFormTypeItem } from "../../../model/report/FormTypeItem";
import PDFExportReport1 from "../report/report1Components/PDFExportReport1";
import { StatusForm } from "../../../common/Constants";

const { confirm } = Modal;

const HEIGHT_FORM_VERSION = window.innerHeight * 0.65;

export interface State {
  signedDocument: File | null;
}

export interface Props {
  formSectionList?: IFormSectionPresentationModel[];
  loading?: boolean;
  translate: any;
  getReferenceValues?: any;
  referenceLoading?: boolean;
  referenceValueList?: IReferenceValue[];
  isEditableForm: boolean;
  onChangeFormVersion?: any;
  onCancel?: any;
  addRow?: any;
  sendFormVersion?: any;
  selectedVersion?: IFormVersion;
  isDraftFormVersion: () => boolean;
  deleteRow?: any;
  isPreviousForm?: boolean;
  onChangeFormLoading?: boolean;
  changeFormAttribute?: any;
  validationErrors?: IFormInputValueValidationError[];
  className?: any;
  firstValidatedAttribute?: IFormInputValueValidationError;
  hideSendFormVersionDialog?: any;
  showSendFormVersionDialog?: any;
  sendFormModalVisible?: boolean;
  formQualifierValidationErrors?: number[];
  hasRightOfEntry?: boolean;
  deleteDraftFormVersion?: any;
  formVersionList?: IFormVersion[];
  pdfReportData?: IFormSectionPresentationModel[];
  pdfReportLoader?: boolean;
  applicant?: IReport1Applicant;
  formVersion?: IFormVersionItem;
  pdfReportVisible?: boolean;
  generatePdfReport?: () => void;
  formType?: IFormTypeItem;
  sendFormVersionLoader?: boolean;
  showSendFormVersionDialogLoader?: boolean;
  changeFormAttributeLoader?: boolean;
  allowedFileTypes: string;
  reportingPeriodId?: number;
  reportingPeriodFormTypeId?: number;
  isPortal?: boolean;
  isSignatureSectionVisible?: boolean;
  isSignedReportRequired?: boolean;
  signedReportValidationError?: boolean;
  onSignedReportFileSelected?: () => void;
}

export default class DisplayForm extends React.Component<Props, State> {
  private signatureSectionRef = React.createRef<SignatureSection>();

  constructor(props: Props) {
    super(props);
    this.state = {
      signedDocument: null
    };
  }

  render() {
    const {
      formSectionList,
      translate,
      getReferenceValues,
      referenceLoading,
      referenceValueList,
      isEditableForm,
      onChangeFormVersion,
      onCancel,
      addRow,
      sendFormVersion,
      selectedVersion,
      isDraftFormVersion,
      deleteRow,
      isPreviousForm,
      onChangeFormLoading,
      changeFormAttribute,
      validationErrors,
      className,
      hideSendFormVersionDialog,
      showSendFormVersionDialog,
      sendFormModalVisible,
      formQualifierValidationErrors,
      hasRightOfEntry,
      deleteDraftFormVersion,
      formVersionList,
      pdfReportData,
      pdfReportLoader,
      applicant,
      formVersion,
      pdfReportVisible,
      generatePdfReport,
      formType,
      sendFormVersionLoader,
      showSendFormVersionDialogLoader,
      changeFormAttributeLoader,
      allowedFileTypes,
      reportingPeriodId,
      reportingPeriodFormTypeId,
      isPortal = false,
      isSignatureSectionVisible = false,
      isSignedReportRequired,
      signedReportValidationError,
      onSignedReportFileSelected
    } = this.props;

    const renderFormSections = (
      isEditable?: boolean,
      isSignatureSectionVisible?: boolean
    ) => {
      let formSectionRenderList: JSX.Element[] = [];
      if (formSectionList) {
        formSectionList.sort((a, b) => {
          return a.orderNumber - b.orderNumber;
        });

        formSectionList.forEach((formSection) => {
          formSectionRenderList.push(
            <div key={formSection.id} id={formSection.id.toString()}>
              <DisplayFormSection
                key={formSection.id}
                allowedFileTypes={allowedFileTypes}
                formSection={formSection}
                translate={translate}
                getReferenceValues={getReferenceValues}
                referenceLoading={referenceLoading}
                referenceValueList={referenceValueList}
                isEditableForm={
                  isEditable !== undefined ? isEditable : isEditableForm
                }
                addRow={addRow}
                deleteRow={deleteRow}
                changeFormAttribute={changeFormAttribute}
                validationErrors={validationErrors}
                formQualifierValidationErrors={formQualifierValidationErrors}
                isPortal={isPortal}
              />
              <Divider
                type={"horizontal"}
                style={{ marginTop: 30, marginBottom: 25 }}
              />
            </div>
          );
        });
      }

      if (isSignatureSectionVisible) {
        formSectionRenderList.push(
          <div key="signature-section" id="signature-section">
            <SignatureSection
              ref={this.signatureSectionRef}
              translate={translate}
              onFileSelected={(file) => {
                this.setState({ signedDocument: file });
                if (file && isSignedReportRequired) {
                  onSignedReportFileSelected?.();
                }
              }}
              generatePdfReport={generatePdfReport}
              pdfReportData={pdfReportData}
              pdfReportLoader={pdfReportLoader}
              applicant={applicant}
              formVersion={formVersion}
              formType={formType}
              reportingPeriodId={reportingPeriodId}
              reportingPeriodFormTypeId={reportingPeriodFormTypeId}
              isPortal={isPortal}
              fileName={`${translate("reports.report_1")}.pdf`}
              isRequired={isSignedReportRequired}
              showError={signedReportValidationError}
            />
            <Divider
              type={"horizontal"}
              style={{ marginTop: 30, marginBottom: 25 }}
            />
          </div>
        );
      }

      return formSectionRenderList;
    };

    const onSendFormVersion = async () => {
      const success = await sendFormVersion(this.state.signedDocument);
      if (!success) {
        return;
      }
      hideSendFormVersionDialog();
      this.setState({ signedDocument: null });
      if (this.signatureSectionRef.current) {
        this.signatureSectionRef.current.reset();
      }
    };

    function showWarningModal() {
      confirm({
        title: translate("form_details.warning_title"),
        content: (
          <div>
            <p>{translate("form_details.warning_message_one")}</p>
            <p>{translate("form_details.warning_message_two")}</p>
          </div>
        ),
        icon: <Icon type="warning" theme="filled" />,
        okText: translate("modal_functions.action_modal.accept"),
        cancelText: translate("modal_functions.action_modal.cancel"),
        onOk() {
          onChangeFormVersion();
        }
      });
    }

    function showDeleteDraftVersionWarningModal() {
      confirm({
        title: translate("form_details.delete_version_modal.title"),
        content: (
          <div>
            <p>
              {translate("form_details.delete_version_modal.warning_message")}
            </p>
          </div>
        ),
        icon: <Icon type="warning" theme="filled" />,
        okText: translate("modal_functions.delete_modal.delete"),
        cancelText: translate("modal_functions.delete_modal.cancel"),
        onOk() {
          deleteDraftFormVersion();
        }
      });
    }

    return (
      <div className={className}>
        <Spin
          tip={this.props.translate("form_details.loading")}
          spinning={this.props.loading}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            {!isEditableForm && (
              <div className={"form-details-header"}>
                {selectedVersion && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%"
                    }}
                  >
                    <div
                      className={"flex-direction-column flex-direction-sm-row"}
                    >
                      <Text className={"form-details-header-label"}>
                        {`${translate("form_details.reporting_period")}: `}
                      </Text>
                      <Text className={"form-details-header-value"}>
                        {selectedVersion.reportingPeriodName}
                      </Text>
                    </div>
                    <div
                      className={"flex-direction-column flex-direction-sm-row"}
                    >
                      <Text className={"form-details-header-label"}>
                        {`${translate("form_details.form_type")}: `}
                      </Text>
                      <Text className={"form-details-header-value"}>
                        {selectedVersion.reportingTypeName}
                      </Text>
                    </div>
                  </div>
                )}
                <div style={{ flexDirection: "column" }}>
                  {generatePdfReport &&
                    selectedVersion &&
                    selectedVersion.status !== StatusForm.DRAFT && (
                      <div className={"pdf-report-container"}>
                        {pdfReportVisible ? (
                          <PDFExportReport1
                            applicant={applicant}
                            translate={translate}
                            data={pdfReportData}
                            title={translate("reports.report_1")}
                            fileName={`${translate("reports.report_1")}.pdf`}
                            loader={pdfReportLoader || false}
                            formVersion={formVersion}
                            formType={formType}
                            reportingPeriodId={reportingPeriodId}
                            reportingPeriodFormTypeId={
                              reportingPeriodFormTypeId
                            }
                            isPortal={isPortal}
                          />
                        ) : (
                          <Button
                            className={"generate-report-btn"}
                            onClick={(e) => generatePdfReport()}
                          >
                            {translate("reports.generate_report")}
                          </Button>
                        )}
                      </div>
                    )}
                  {!isPreviousForm && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignSelf: "flex-end",
                        width: "100%",
                        justifyContent: "flex-end"
                      }}
                    >
                      {formVersionList?.length === 0 && hasRightOfEntry && (
                        <Button
                          disabled={onChangeFormLoading}
                          onClick={onChangeFormVersion}
                        >
                          {translate("form_details.new_draft_version")}
                        </Button>
                      )}
                      {selectedVersion && (
                        <Button
                          type={"default"}
                          style={{
                            alignSelf: "flex-end",
                            marginRight: 10,
                            display:
                              !selectedVersion?.isDraftVersion() &&
                              !hasRightOfEntry
                                ? "none"
                                : "initial"
                          }}
                          disabled={onChangeFormLoading}
                          onClick={
                            isDraftFormVersion() &&
                            !selectedVersion?.isDraftVersion()
                              ? showWarningModal
                              : onChangeFormVersion
                          }
                        >
                          {selectedVersion.isDraftVersion()
                            ? translate("form_details.change")
                            : translate("form_details.new_draft_version")}
                        </Button>
                      )}
                      {selectedVersion && selectedVersion.isDraftVersion() && (
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <Button
                            type={"primary"}
                            style={{ alignSelf: "flex-end", marginRight: 10 }}
                            loading={showSendFormVersionDialogLoader || false}
                            disabled={changeFormAttributeLoader || false}
                            onClick={showSendFormVersionDialog}
                          >
                            {translate("form_details.send")}
                          </Button>

                          <Button
                            type={"danger"}
                            style={{ alignSelf: "flex-end" }}
                            onClick={showDeleteDraftVersionWarningModal}
                          >
                            {translate("form_details.delete_draft_version")}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div
              style={{ overflowY: "auto", height: window.innerHeight * 0.7 }}
            >
              {renderFormSections()}
            </div>

            {isEditableForm && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  paddingTop: 10,
                  marginTop: 10,
                  borderTopWidth: 1,
                  borderTopColor: "#f0f0f0",
                  borderTopStyle: "solid",
                  width: "100%",
                  justifyContent: "flex-end"
                }}
              >
                <Button
                  type={"link"}
                  style={{ marginRight: 10 }}
                  onClick={onCancel}
                >
                  {translate("form_details.back")}
                </Button>

                <Button
                  type={"primary"}
                  loading={showSendFormVersionDialogLoader || false}
                  disabled={changeFormAttributeLoader || false}
                  onClick={showSendFormVersionDialog}
                >
                  {translate("form_details.send")}
                </Button>
              </div>
            )}
          </div>
        </Spin>
        <Modal
          visible={sendFormModalVisible}
          title={translate("form_details.confirm_title")}
          onOk={onSendFormVersion}
          onCancel={() => {
            hideSendFormVersionDialog();
            this.setState({ signedDocument: null });
            if (this.signatureSectionRef.current) {
              this.signatureSectionRef.current.reset();
            }
          }}
          okText={translate("modal_functions.send_modal.send")}
          okButtonProps={{ loading: sendFormVersionLoader || false }}
          cancelText={translate("modal_functions.send_modal.cancel")}
          width={"70%"}
        >
          <div style={{ padding: 5 }}>
            <Alert
              message={translate("form_details.confirm_message")}
              description={translate("form_details.preview_form_message")}
              type="info"
              style={{ margin: "10px 0" }}
            />
            <div style={{ overflowY: "auto", height: HEIGHT_FORM_VERSION }}>
              {renderFormSections(false, isSignatureSectionVisible)}
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}
